/**
 * Query Parameters Helper
 * 
 * Provides utilities for managing URL query parameters in a type-safe and consistent way.
 * This helper ensures query parameters are properly encoded/decoded and handles edge cases.
 */

/**
 * Get a query parameter value from URLSearchParams
 * @param {URLSearchParams} searchParams - The search params object from useSearchParams
 * @param {string} key - The query parameter key
 * @param {string|null} defaultValue - Default value if key doesn't exist
 * @returns {string|null} The parameter value or default
 */
export const getQueryParam = (searchParams, key, defaultValue = null) => {
  if (!searchParams || !key) return defaultValue;
  const value = searchParams.get(key);
  return value !== null ? decodeURIComponent(value) : defaultValue;
};

/**
 * Set a query parameter and return the updated search string
 * @param {URLSearchParams} searchParams - Current search params
 * @param {string} key - The query parameter key
 * @param {string|null|undefined} value - The value to set (null/undefined removes the param)
 * @returns {URLSearchParams} New URLSearchParams instance with updated value
 */
export const setQueryParam = (searchParams, key, value) => {
  const newParams = new URLSearchParams(searchParams);
  if (value === null || value === undefined || value === '') {
    newParams.delete(key);
  } else {
    newParams.set(key, encodeURIComponent(String(value)));
  }
  return newParams;
};

/**
 * Set multiple query parameters at once
 * @param {URLSearchParams} searchParams - Current search params
 * @param {Object<string, string|null|undefined>} params - Object with key-value pairs
 * @returns {URLSearchParams} New URLSearchParams instance with updated values
 */
export const setMultipleQueryParams = (searchParams, params) => {
  const newParams = new URLSearchParams(searchParams);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, encodeURIComponent(String(value)));
    }
  });
  return newParams;
};

/**
 * Remove a query parameter
 * @param {URLSearchParams} searchParams - Current search params
 * @param {string} key - The query parameter key to remove
 * @returns {URLSearchParams} New URLSearchParams instance without the key
 */
export const removeQueryParam = (searchParams, key) => {
  const newParams = new URLSearchParams(searchParams);
  newParams.delete(key);
  return newParams;
};

/**
 * Remove multiple query parameters
 * @param {URLSearchParams} searchParams - Current search params
 * @param {string[]} keys - Array of keys to remove
 * @returns {URLSearchParams} New URLSearchParams instance without the keys
 */
export const removeMultipleQueryParams = (searchParams, keys) => {
  const newParams = new URLSearchParams(searchParams);
  keys.forEach(key => newParams.delete(key));
  return newParams;
};

/**
 * Get all query parameters as an object
 * @param {URLSearchParams} searchParams - Current search params
 * @returns {Object<string, string>} Object with all query parameters
 */
export const getAllQueryParams = (searchParams) => {
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = decodeURIComponent(value);
  });
  return params;
};

/**
 * Check if a query parameter exists
 * @param {URLSearchParams} searchParams - Current search params
 * @param {string} key - The query parameter key
 * @returns {boolean} True if the parameter exists
 */
export const hasQueryParam = (searchParams, key) => {
  return searchParams && searchParams.has(key);
};

/**
 * Build a navigation path with query parameters
 * @param {string} basePath - Base path (e.g., '/company/123/chargePoint/456')
 * @param {URLSearchParams} searchParams - Query parameters to append
 * @returns {string} Full path with query string
 */
export const buildPathWithQuery = (basePath, searchParams) => {
  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Query Parameters Helper - Main export object
 */
const queryParamsHelper = {
  get: getQueryParam,
  set: setQueryParam,
  setMultiple: setMultipleQueryParams,
  remove: removeQueryParam,
  removeMultiple: removeMultipleQueryParams,
  getAll: getAllQueryParams,
  has: hasQueryParam,
  buildPath: buildPathWithQuery,
};

export default queryParamsHelper;
